import { useEffect, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Spinner,
} from "@chakra-ui/react";
import { Organization, Tenant, TenantMembershipRoleName } from "@bluefalcon/users-management-client-fetch";
import { SearchableDropdown } from "../components/base/searchable-dropdown/searchable-dropdown.comp";
import { tenantsService, organizationService } from "../services";
import { toSpacedPascalCase } from "@bluefalcon/utils-package";

type GrantOrgMembershipModalProps = {
    initialTenant?: Tenant;
    initialOrg?: Organization;
    onGrant: (tenantId: string, orgId: string, role: TenantMembershipRoleName) => void;
    isOpen: boolean;
    onClose: () => void;
    isLoading: boolean;
};

export const GrantOrgMembershipModal = ({
    initialTenant,
    initialOrg,
    onGrant,
    isOpen,
    onClose,
    isLoading,
}: GrantOrgMembershipModalProps) => {
    const [selectedTenant, setSelectedTenant] = useState<Tenant>();
    const [selectedOrg, setSelectedOrg] = useState<Organization>();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [orgs, setOrgs] = useState<Organization[]>([]);
    const [selectedRole, setSelectedRole] = useState<TenantMembershipRoleName>('member');

    const handleGrant = () => {
        if (selectedTenant && selectedOrg && selectedRole) {
            onGrant(selectedTenant.id, selectedOrg.id, selectedRole);
        }
    };

    const onSelectTenant = (name?: string) => {
        setSelectedTenant(tenants.find((t) => t.name === name));
    };

    const onSelectOrg = (name?: string) => {
        setSelectedOrg(orgs.find((o) => o.name === name));
    };

    const fetchTenants = async () => {
        const res = await tenantsService.searchTenants({});
        setTenants(res.results);
    };

    const fetchUsers = async () => {
        const res = await organizationService.searchOrganizations({});
        setOrgs(res.results);
    };

    useEffect(() => {
        if (isOpen) {
            if (!initialTenant) {
                fetchTenants();
            }
            if (!initialOrg) {
                fetchUsers();
            }
        }
    }, [isOpen]);

    useEffect(() => {
        setSelectedTenant(initialTenant);
        setSelectedOrg(initialOrg);
    }, [initialTenant, initialOrg]);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            {!isLoading ? <ModalContent>
                <ModalHeader>Grant Role</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <FormControl isRequired>
                        <FormLabel>Tenant</FormLabel>
                        <SearchableDropdown
                            items={tenants.map((t) => t.name)}
                            placeholder="Tenant"
                            onSelect={(name) => onSelectTenant(name)}
                            isDisabled={!!initialTenant}
                            value={initialTenant?.name ?? selectedTenant?.name}
                            titleTag="Tenant"
                        />
                    </FormControl>
                    <FormControl isRequired>
                        <FormLabel>Organization</FormLabel>
                        <SearchableDropdown
                            items={orgs.map((o) => o.name)}
                            placeholder="Organization"
                            onSelect={(name) => onSelectOrg(name)}
                            isDisabled={!!initialOrg}
                            value={initialOrg ? initialOrg.name : selectedOrg?.name}
                            titleTag="Organization"
                        />
                    </FormControl>
                    <FormControl isRequired>
                        <FormLabel>Role</FormLabel>
                        <SearchableDropdown
                            items={['Member', 'Owner']}
                            placeholder="Role"
                            onSelect={(role) => setSelectedRole(role?.toLowerCase() as TenantMembershipRoleName)}
                            value={toSpacedPascalCase(selectedRole)}
                            titleTag="Role"
                        />
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" onClick={onClose} mr={3}>Cancel</Button>
                    <Button colorScheme="primary" onClick={handleGrant}>Grant</Button>
                </ModalFooter>
            </ModalContent> : <Spinner />}
        </Modal>
    );
};
